import React, { useState, useEffect } from 'react';
import {
  Card,
  Form,
  Switch,
  Button,
  Row,
  Col,
  Spin,
  Space,
  Tooltip,
  Divider,
} from 'antd';
import {
  InfoCircleOutlined,
  SaveOutlined,
  ReloadOutlined,
  SwapOutlined,
  BankOutlined,
  FileTextOutlined,
  UserOutlined,
  CalendarOutlined,
} from '@ant-design/icons';
import { customBaseUrl, httpGet, httpPut } from '../../store/http';
import { NotificationManager } from 'react-notifications';
import Swal from 'sweetalert2';
import moment from 'moment';

const TransactionTypesSettings = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastUpdatedBy, setLastUpdatedBy] = useState('');
  const [lastUpdatedAt, setLastUpdatedAt] = useState('');
  const [form] = Form.useForm();

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await httpGet(
        `${customBaseUrl.terminal}/api/v1/admin/config/transaction-types`,
      );
      if (res?.success || res?.status) {
        const config = res.data || res;
        form.setFieldsValue({
          interBankTransferEnabled: !!config.interBankTransferEnabled,
          intraBankTransferEnabled: !!config.intraBankTransferEnabled,
          billsPaymentEnabled: !!config.billsPaymentEnabled,
        });
        setLastUpdatedBy(config.updatedBy || '');
        setLastUpdatedAt(config.updatedAt || '');
      } else {
        NotificationManager.error(
          res?.message || 'Failed to load transaction-type toggles.',
        );
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error(
        'An error occurred while loading transaction-type toggles.',
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleSave = async (values) => {
    setSaving(true);
    try {
      const payload = {
        interBankTransferEnabled: !!values.interBankTransferEnabled,
        intraBankTransferEnabled: !!values.intraBankTransferEnabled,
        billsPaymentEnabled: !!values.billsPaymentEnabled,
      };

      const res = await httpPut(
        `${customBaseUrl.terminal}/api/v1/admin/config/transaction-types`,
        payload,
      );
      if (res?.success || res?.status || res?.status === 'SUCCESS') {
        Swal.fire({
          title: 'Configuration Saved',
          text: 'Transaction-type toggles have been updated successfully.',
          icon: 'success',
          confirmButtonColor: '#ff6700',
        });
        fetchConfig();
      } else {
        NotificationManager.error(
          res?.message || 'Failed to save configuration.',
        );
      }
    } catch (err) {
      console.error(err);
      NotificationManager.error(
        'An error occurred while saving configuration.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Spin spinning={loading} tip="Loading configuration...">
      <div style={{ padding: '8px 0' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
            padding: '12px 16px',
            background: '#fafafa',
            border: '1px solid #f0f0f0',
            borderRadius: 8,
          }}
        >
          <span style={{ color: '#595959', fontSize: '13px' }}>
            Enable or disable global transaction types available across POS
            terminals.
          </span>
          <Button
            size="small"
            type="default"
            icon={<ReloadOutlined />}
            onClick={fetchConfig}
          >
            Reload Config
          </Button>
        </div>

        <Card
          title={
            <Space>
              <SwapOutlined style={{ color: '#ff6700' }} />
              <span>Global Transaction-Type Toggles</span>
            </Space>
          }
          style={{
            borderRadius: 12,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            border: '1px solid #f0f0f0',
          }}
        >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
            requiredMark={false}
          >
            <Row gutter={[24, 24]}>
              <Col xs={24} md={8}>
                <Form.Item
                  name="interBankTransferEnabled"
                  label={
                    <Space>
                      <BankOutlined />
                      <strong>Inter-Bank Transfer</strong>
                      <Tooltip title="When enabled, POS terminals can initiate transfers to other banks.">
                        <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                      </Tooltip>
                    </Space>
                  }
                  valuePropName="checked"
                >
                  <Switch checkedChildren="ON" unCheckedChildren="OFF" />
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item
                  name="intraBankTransferEnabled"
                  label={
                    <Space>
                      <SwapOutlined />
                      <strong>Intra-Bank Transfer</strong>
                      <Tooltip title="When enabled, POS terminals can initiate transfers within Wayabank.">
                        <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                      </Tooltip>
                    </Space>
                  }
                  valuePropName="checked"
                >
                  <Switch checkedChildren="ON" unCheckedChildren="OFF" />
                </Form.Item>
              </Col>

              <Col xs={24} md={8}>
                <Form.Item
                  name="billsPaymentEnabled"
                  label={
                    <Space>
                      <FileTextOutlined />
                      <strong>Bills Payment</strong>
                      <Tooltip title="When enabled, POS terminals can process bills payment transactions.">
                        <InfoCircleOutlined style={{ color: '#bfbfbf' }} />
                      </Tooltip>
                    </Space>
                  }
                  valuePropName="checked"
                >
                  <Switch checkedChildren="ON" unCheckedChildren="OFF" />
                </Form.Item>
              </Col>
            </Row>

            <Divider style={{ margin: '24px 0 16px 0' }} />

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {lastUpdatedBy || lastUpdatedAt ? (
                <div
                  style={{
                    display: 'flex',
                    gap: 16,
                    color: '#8c8c8c',
                    fontSize: '12px',
                    alignItems: 'center',
                  }}
                >
                  {lastUpdatedBy && (
                    <span>
                      <UserOutlined style={{ marginRight: 4 }} />
                      Last updated by: <strong>{lastUpdatedBy}</strong>
                    </span>
                  )}
                  {lastUpdatedAt && (
                    <span>
                      <CalendarOutlined style={{ marginRight: 4 }} />
                      Updated at:{' '}
                      <strong>
                        {moment(lastUpdatedAt).format('YYYY-MM-DD HH:mm:ss')}
                      </strong>
                    </span>
                  )}
                </div>
              ) : (
                <div />
              )}
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
                style={{
                  background: '#ff6700',
                  borderColor: '#ff6700',
                  height: 40,
                  borderRadius: 6,
                  fontWeight: 600,
                  boxShadow: '0 4px 10px rgba(255, 103, 0, 0.2)',
                }}
              >
                Save Configuration
              </Button>
            </div>
          </Form>
        </Card>
      </div>
    </Spin>
  );
};

export default TransactionTypesSettings;
